import { ALL_ORGANISATIONS_QUERY } from "@lib/graphql/queries";
import type { Organisation } from "@lib/types";
import { ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { useQuery } from "urql";
import { useCurrentUserShell } from "@/contexts/CurrentUserContext";

interface OrgSwitcherProps {
  currentOrganisation: Organisation | null;
}

async function postSwitch(organisationId: string): Promise<boolean> {
  const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
  const response = await fetch("/organisations/switch", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
    body: `organisation_id=${encodeURIComponent(organisationId)}`,
  });
  return response.ok;
}

export function OrgSwitcher({ currentOrganisation }: OrgSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { currentUser, organisations: ownOrganisations } = useCurrentUserShell();

  const isSuperuser: boolean = currentUser?.superuser ?? false;

  // Superusers can see ALL orgs in the switcher. Lazily fetched so we don't
  // pay for it on every page load for normal users.
  const [allOrgsResult] = useQuery({
    query: ALL_ORGANISATIONS_QUERY,
    pause: !isSuperuser,
  });

  const organisations: Organisation[] = isSuperuser
    ? allOrgsResult.data?.allOrganisations || ownOrganisations
    : ownOrganisations;

  const filtered = useMemo(() => {
    if (!search.trim()) return organisations;
    const needle = search.toLowerCase();
    return organisations.filter((o) => o.name?.toLowerCase().includes(needle));
  }, [organisations, search]);

  if (!isSuperuser && organisations.length <= 1) {
    return (
      <span className="text-sm font-medium text-text-primary">
        {currentOrganisation?.name || "No Organisation"}
      </span>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-md px-2 py-1 text-sm font-medium text-text-primary hover:bg-card-hover"
      >
        {currentOrganisation?.name || "Select Organisation"}
        <ChevronDown size={16} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-72 rounded-md bg-card py-1 shadow-lg ring-1 ring-ring">
          {isSuperuser && (
            <div className="px-3 pb-2 pt-1">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search organisations…"
                className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-text-primary placeholder:text-text-faint focus:border-accent focus:outline-none"
              />
            </div>
          )}
          <div className="max-h-80 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-4 py-2 text-sm text-text-faint">No matches.</div>
            )}
            {filtered.map((org) => (
              <button
                key={org.id}
                type="button"
                onClick={async () => {
                  setOpen(false);
                  if (await postSwitch(org.id)) {
                    window.location.reload();
                  }
                }}
                className={`block w-full px-4 py-2 text-left text-sm ${
                  org.id === currentOrganisation?.id
                    ? "text-accent font-medium"
                    : "text-text-secondary hover:bg-card-hover"
                }`}
              >
                {org.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
